import * as echarts from 'echarts';

// 中国地图的大致边界范围（经纬度）
export const CHINA_MAP_BOUNDS = {
  minLng: 73,   // 最西端
  maxLng: 135,  // 最东端
  minLat: 18,   // 最南端
  maxLat: 54    // 最北端
};

/**
 * 检查地图是否完全在可视区域内
 */
export const checkMapBounds = (chartInstance: echarts.ECharts): boolean => {
  try {
    // 获取地图四个角的像素坐标
    const corners = [
      [CHINA_MAP_BOUNDS.minLng, CHINA_MAP_BOUNDS.minLat], // 左下
      [CHINA_MAP_BOUNDS.maxLng, CHINA_MAP_BOUNDS.minLat], // 右下
      [CHINA_MAP_BOUNDS.maxLng, CHINA_MAP_BOUNDS.maxLat], // 右上
      [CHINA_MAP_BOUNDS.minLng, CHINA_MAP_BOUNDS.maxLat]  // 左上
    ];

    const pixelPoints = corners.map(coord => 
      chartInstance.convertToPixel('geo', coord)
    );

    const containerWidth = chartInstance.getWidth();
    const containerHeight = chartInstance.getHeight();

    // 检查所有角点是否都在容器内
    const allPointsInBounds = pixelPoints.every(point => {
      if (!point || !Array.isArray(point)) return false;
      const [x, y] = point;
      return x >= 0 && x <= containerWidth && y >= 0 && y <= containerHeight;
    });

    console.log('🔍 地图边界检测:', {
      containerSize: { width: containerWidth, height: containerHeight },
      pixelPoints,
      allPointsInBounds
    });

    return allPointsInBounds;
  } catch (error) {
    console.warn('边界检测失败:', error);
    return true; // 默认认为正常
  }
};

/**
 * 自动调整地图直到完全显示
 */
export const autoAdjustMapToFit = (chartInstance: echarts.ECharts): Promise<boolean> => {
  return new Promise((resolve) => {
    let attempts = 0;
    const maxAttempts = 10;
    
    const adjust = () => {
      if (attempts >= maxAttempts) {
        console.warn('⚠️ 地图自动调整达到最大尝试次数');
        resolve(false);
        return;
      }
      
      const isInBounds = checkMapBounds(chartInstance);
      if (isInBounds) {
        console.log('✅ 地图已完全显示');
        resolve(true);
        return;
      }
      
      try {
        // 获取当前配置
        const currentOption = chartInstance.getOption();
        const currentGeo = currentOption.geo?.[0];
        
        if (currentGeo) {
          // 如果使用layoutSize，减少layoutSize
          if (currentGeo.layoutSize) {
            const currentSize = parseFloat(currentGeo.layoutSize.toString().replace('%', ''));
            const newSize = Math.max(currentSize * 0.9, 50); // 最小50%
            
            chartInstance.setOption({
              geo: {
                layoutSize: `${newSize}%`
              }
            });
          } 
          // 如果使用zoom，减少zoom
          else if (currentGeo.zoom) {
            const currentZoom = currentGeo.zoom;
            const newZoom = Math.max(currentZoom * 0.9, 0.3); // 最小0.3倍
            
            chartInstance.setOption({
              geo: {
                zoom: newZoom
              }
            });
          }
        }
        
        attempts++;
        setTimeout(adjust, 200); // 延迟检查，等待渲染完成
      } catch (error) {
        console.error('地图调整失败:', error);
        resolve(false);
      }
    };
    
    // 初始延迟，等待地图渲染完成
    setTimeout(adjust, 300);
  });
};

/**
 * 计算最佳的layoutSize，确保地图在90%高度容器内完整显示
 */
export const calculateOptimalLayoutSize = (
  containerWidth: number, 
  containerHeight: number
): string => {
  const containerRatio = containerWidth / containerHeight;
  const mapRatio = (CHINA_MAP_BOUNDS.maxLng - CHINA_MAP_BOUNDS.minLng) / 
                   (CHINA_MAP_BOUNDS.maxLat - CHINA_MAP_BOUNDS.minLat);
  
  let layoutSize;
  
  if (containerRatio > mapRatio) {
    // 容器更宽，高度是限制因子
    // 在90%高度容器内，可以更激进地使用空间
    layoutSize = Math.min(98, (mapRatio / containerRatio) * 105); // 提高到105%基数
  } else {
    // 容器更高，宽度是限制因子
    layoutSize = Math.min(98, (containerRatio / mapRatio) * 105);
  }
  
  // 在大容器内，最小尺寸可以更大
  layoutSize = Math.max(layoutSize, 85); // 从60%提升到85%
  
  return `${layoutSize.toFixed(0)}%`;
};

/**
 * 验证地图配置是否合理
 */
export const validateMapConfig = (config: any): boolean => {
  try {
    const geo = config.geo;
    if (!geo) return false;
    
    // 检查必要的配置项
    if (geo.layoutSize) {
      const size = parseFloat(geo.layoutSize.toString().replace('%', ''));
      if (size < 10 || size > 100) return false;
    }
    
    if (geo.zoom) {
      if (geo.zoom < 0.1 || geo.zoom > 10) return false;
    }
    
    if (geo.center && Array.isArray(geo.center)) {
      const [lng, lat] = geo.center;
      if (lng < 70 || lng > 140 || lat < 10 || lat > 60) return false;
    }
    
    return true;
  } catch (error) {
    console.error('地图配置验证失败:', error);
    return false;
  }
};
